import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Battery, Shield, Activity, TrendingUp, TrendingDown, AlertTriangle, MapPin, Menu, LogOut, Zap, BarChart3, ListTodo, FileDown, Plus, Thermometer, Lock, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    asset_type: 'Fixed',
    location: '',
    latitude: '',
    longitude: '',
    serial_no: '',
    manufacture_date: '',
    commissioning_date: new Date().toISOString().split('T')[0],
    warranty_degradation_rate: 1.0,
    actual_degradation_rate: 1.0,
    invoice_date: '',
    engineer_installer: '',
    installed_per_manual: true,
    received_undamaged: true,
    owner: 'SodiumQ Operations'
  });
  const [engineerInstallers, setEngineerInstallers] = useState([]);
  const [weatherData, setWeatherData] = useState({});  // Store weather for each asset
  const [scaSummary, setScaSummary] = useState(null);  // SCA alerts summary
  const [scaAlertsByAsset, setScaAlertsByAsset] = useState({});  // SCA alerts by asset ID
  const [cyberDashboard, setCyberDashboard] = useState(null);  // Cyber security summary

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/login');
      return;
    }

    setUser(JSON.parse(userData));
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const [dashboardRes, assetsRes, installersRes, scaSummaryRes, cyberRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, config),
        axios.get(`${API}/assets`, config),
        axios.get(`${API}/assets/config/engineer-installers`, config),
        axios.get(`${API}/sca-alerts/summary`, config).catch(err => ({ data: null })),
        axios.get(`${API}/cyber/dashboard`, config).catch(err => ({ data: null }))
      ]);

      setDashboardData(dashboardRes.data);
      setAssets(assetsRes.data);
      setEngineerInstallers(installersRes.data.installers || []);
      setScaSummary(scaSummaryRes.data);
      setCyberDashboard(cyberRes.data);
      
      // Load weather data for all assets
      const weatherPromises = assetsRes.data.map(asset => 
        axios.get(`${API}/weather/${asset.id}`, config).catch(err => null)
      );
      const weatherResults = await Promise.all(weatherPromises);
      
      const weatherMap = {};
      weatherResults.forEach((result, index) => {
        if (result && result.data) {
          weatherMap[assetsRes.data[index].id] = result.data;
        }
      });
      setWeatherData(weatherMap);
      
      // Load SCA alerts for all assets
      const scaPromises = assetsRes.data.map(asset => 
        axios.get(`${API}/sca-alerts/asset/${asset.id}`, config).catch(err => ({ data: [] }))
      );
      const scaResults = await Promise.all(scaPromises);
      
      const scaMap = {};
      scaResults.forEach((result, index) => {
        if (result && result.data) {
          scaMap[assetsRes.data[index].id] = result.data.filter(alert => alert.status === 'Open');
        }
      });
      setScaAlertsByAsset(scaMap);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getHealthColor = (score) => {
    if (score >= 90) return 'text-emerald-500';
    if (score >= 75) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getSafetyColor = (score) => {
    if (score >= 95) return 'text-emerald-500';
    if (score >= 85) return 'text-yellow-500';
    return 'text-red-500';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
    } catch (error) {
      return dateString;
    }
  };

  const handleCheckboxUpdate = async (assetId, field, value) => {
    try {
      const token = localStorage.getItem('token');
      const payload = {};
      payload[field] = value;
      
      await axios.patch(`${API}/assets/${assetId}/checkboxes?${field}=${value}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setAssets(assets.map(a => a.id === assetId ? {...a, [field]: value} : a));
      toast.success('Asset updated successfully');
    } catch (error) {
      console.error('Checkbox update error:', error);
      toast.error('Failed to update asset');
      // Reload data to revert UI
      loadData();
    }
  };

  const handleFieldUpdate = async (assetId, field, value) => {
    try {
      const token = localStorage.getItem('token');
      const payload = { 
        [field]: (field === 'warranty_degradation_rate' || field === 'actual_degradation_rate') 
          ? parseFloat(value) 
          : value 
      };
      
      await axios.patch(`${API}/assets/${assetId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reload data to get updated SOH and other calculated fields
      await loadData();
      toast.success('Asset updated successfully');
    } catch (error) {
      console.error('Field update error:', error);
      toast.error('Failed to update asset');
      loadData();
    }
  };

  const downloadFleetReport = async () => {
    const toastId = toast.loading('Generating fleet report...');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/fleet-summary/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 30000 // 30 seconds timeout for PDF generation
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sodiumq_fleet_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Fleet report downloaded successfully', { id: toastId });
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download report', { id: toastId });
    }
  };

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const assetData = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude)
      };
      
      await axios.post(`${API}/assets`, assetData, config);
      toast.success('Asset created successfully');
      setShowCreateModal(false);
      setFormData({
        name: '',
        asset_type: 'Fixed',
        location: '',
        latitude: '',
        longitude: '',
        serial_no: '',
        manufacture_date: '',
        commissioning_date: new Date().toISOString().split('T')[0],
        warranty_degradation_rate: 1.0,
        invoice_date: '',
        engineer_installer: '',
        installed_per_manual: true,
        received_undamaged: true,
        owner: 'SodiumQ Operations'
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create asset');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="dashboard-page">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-gray-200 transition-all duration-300 flex flex-col`}>
        <div className="p-6 border-b border-gray-200 flex items-center justify-center">
          <img 
            src="/sodiumq-logo.png" 
            alt="SodiumQ Logo" 
            className={`${sidebarOpen ? 'h-10' : 'h-8'} w-auto transition-all duration-300`}
          />
        </div>

        <nav className="flex-1 p-4 space-y-2">
          <Button
            variant="ghost"
            className="w-full justify-start text-emerald-600 bg-emerald-50 hover:bg-emerald-100 hover:text-emerald-700"
            onClick={() => navigate('/dashboard')}
            data-testid="nav-dashboard"
          >
            <BarChart3 className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">Dashboard</span>}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100"
            onClick={() => navigate('/map')}
            data-testid="nav-map"
          >
            <MapPin className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">Map View</span>}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100"
            onClick={() => navigate('/alerts')}
            data-testid="nav-alerts"
          >
            <AlertTriangle className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">Alerts</span>}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100"
            onClick={() => navigate('/sca-alerts')}
            data-testid="nav-sca-alerts"
          >
            <Shield className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">SCA Alerts</span>}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100"
            onClick={() => navigate('/cyber-security')}
            data-testid="nav-cyber-security"
          >
            <Lock className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">Cyber-Physical Security</span>}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100"
            onClick={() => navigate('/tasks')}
            data-testid="nav-tasks"
          >
            <ListTodo className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">Tasks</span>}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100"
            onClick={() => navigate('/reports/soh')}
            data-testid="nav-soh-report"
          >
            <FileDown className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">State of Health Report</span>}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100"
            onClick={() => navigate('/reports/soc')}
            data-testid="nav-soc-report"
          >
            <Battery className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">State of Charge Report</span>}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100"
            onClick={() => navigate('/reports/degradation')}
            data-testid="nav-degradation-report"
          >
            <TrendingDown className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">Degradation Rate Report</span>}
          </Button>
        </nav>

        <div className="p-4 border-t border-gray-200">
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-black mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Fleet Dashboard
              </h1>
              <p className="text-gray-600">Welcome back, {user?.name}</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={downloadFleetReport}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                data-testid="download-fleet-report-btn"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Export Report
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-700 hover:text-black hover:bg-gray-100"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="toggle-sidebar-btn"
              >
                <Menu className="w-6 h-6" />
              </Button>
            </div>
          </div>

          {/* Guest Welcome Banner */}
          {user?.role === 'guest' && (
            <div className="mb-6 px-5 py-4 bg-gradient-to-r from-slate-50 to-blue-50 border border-blue-200 rounded-xl flex items-center gap-4" data-testid="guest-welcome-banner">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Eye className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Viewer Mode</p>
                <p className="text-xs text-slate-500">You have read-only access to all dashboards, reports, and asset data. Contact an administrator for edit permissions.</p>
              </div>
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card 
              className="bg-white border-gray-200 hover:shadow-lg transition-all cursor-pointer"
              onClick={() => {
                // Scroll to assets grid
                const assetsGrid = document.querySelector('[data-testid="assets-grid"]');
                if (assetsGrid) {
                  assetsGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                  Total Assets
                  <Battery className="w-5 h-5 text-emerald-500" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {dashboardData?.total_assets || 0}
                </div>
                <p className="text-sm text-gray-600 mt-1">{dashboardData?.active_assets || 0} active</p>
              </CardContent>
            </Card>

            <Card 
              className="bg-white border-gray-200 hover:shadow-lg transition-all cursor-pointer"
              onClick={() => {
                // Scroll to assets and show tooltip or highlight health scores
                const assetsGrid = document.querySelector('[data-testid="assets-grid"]');
                if (assetsGrid) {
                  assetsGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
                toast.info('Asset health scores are displayed on each card below');
              }}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                  Avg Health Score
                  <Activity className="w-5 h-5 text-emerald-500" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold ${getHealthColor(dashboardData?.avg_health_score || 0)}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {(dashboardData?.avg_health_score || 0).toFixed(2)}%
                </div>
                <p className="text-sm text-gray-600 mt-1">Fleet average</p>
              </CardContent>
            </Card>

            <Card 
              className="bg-white border-gray-200 hover:shadow-lg transition-all cursor-pointer"
              onClick={() => {
                const assetsGrid = document.querySelector('[data-testid="assets-grid"]');
                if (assetsGrid) {
                  assetsGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
                toast.info('Safety scores are displayed on each asset card below');
              }}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                  Safety Score
                  <Shield className="w-5 h-5 text-emerald-500" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold ${getSafetyColor(dashboardData?.avg_safety_score || 0)}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {(dashboardData?.avg_safety_score || 0).toFixed(2)}%
                </div>
                <p className="text-sm text-gray-600 mt-1">Fleet average</p>
              </CardContent>
            </Card>

            <Card 
              className="bg-white border-gray-200 hover:shadow-lg transition-all cursor-pointer"
              onClick={() => navigate('/alerts')}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                  Active Alerts
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {(dashboardData?.critical_alerts || 0) + (dashboardData?.warning_alerts || 0)}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {dashboardData?.critical_alerts || 0} critical
                </p>
              </CardContent>
            </Card>

            <Card 
              className="bg-white border-gray-200 hover:shadow-lg transition-all cursor-pointer" 
              onClick={() => navigate('/alerts?filter=temperature')}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                  Extreme Temps
                  <Thermometer className="w-5 h-5 text-orange-500" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {Object.values(weatherData).filter(w => 
                    w?.forecast?.some(day => day.temp_max > 40 || day.temp_min < -5)
                  ).length}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Sites with alerts
                </p>
              </CardContent>
            </Card>

            {scaSummary && (
              <Card className="bg-white border-gray-200 hover:shadow-lg transition-all cursor-pointer" onClick={() => navigate('/sca-alerts')}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                    Availability Risk
                    <TrendingDown className={`w-5 h-5 ${scaSummary.availability_risk_index < 30 ? 'text-green-500' : scaSummary.availability_risk_index < 60 ? 'text-yellow-500' : 'text-red-500'}`} />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-3xl font-bold ${scaSummary.availability_risk_index < 30 ? 'text-green-600' : scaSummary.availability_risk_index < 60 ? 'text-yellow-600' : 'text-red-600'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {scaSummary.availability_risk_index}%
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {scaSummary.total_alerts} SCA alerts
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Cyber Threat Level Card */}
            <Card
              className="bg-white border-gray-200 hover:shadow-lg transition-all cursor-pointer relative overflow-hidden"
              onClick={() => navigate('/cyber-security')}
              data-testid="overview-cyber-threat"
            >
              {cyberDashboard && cyberDashboard.critical_alerts > 0 && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-red-500 animate-pulse" />
              )}
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                  Cyber Threat
                  <Lock className={`w-5 h-5 ${!cyberDashboard || cyberDashboard.total_alerts === 0 ? 'text-green-500' : cyberDashboard.critical_alerts > 0 ? 'text-red-500' : 'text-yellow-500'}`} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold ${!cyberDashboard || cyberDashboard.total_alerts === 0 ? 'text-green-600' : cyberDashboard.critical_alerts > 0 ? 'text-red-600' : 'text-yellow-600'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {cyberDashboard ? cyberDashboard.total_alerts : 0}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {cyberDashboard ? `${cyberDashboard.critical_alerts} critical` : 'No data'}
                </p>
              </CardContent>
            </Card>

            {/* Active Attacks Card */}
            <Card
              className="bg-white border-gray-200 hover:shadow-lg transition-all cursor-pointer relative overflow-hidden"
              onClick={() => navigate('/cyber-security?tab=incidents')}
              data-testid="overview-active-attacks"
            >
              {cyberDashboard && cyberDashboard.active_incidents > 0 && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-orange-500 animate-pulse" />
              )}
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                  Active Attacks
                  <Shield className={`w-5 h-5 ${!cyberDashboard || cyberDashboard.active_incidents === 0 ? 'text-green-500' : 'text-red-500'}`} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold ${!cyberDashboard || cyberDashboard.active_incidents === 0 ? 'text-green-600' : 'text-red-600'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {cyberDashboard ? cyberDashboard.active_incidents : 0}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {cyberDashboard ? `${cyberDashboard.high_alerts} high severity` : 'No data'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Assets Grid */}
          <Card className="bg-white border-gray-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>Battery Assets</CardTitle>
                {user?.role !== 'guest' && (
                  <Button
                    onClick={() => setShowCreateModal(true)}
                    size="sm"
                    className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                    data-testid="add-asset-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Asset
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="assets-grid">
                {assets.map((asset) => (
                  <div
                    key={asset.id}
                    onClick={() => navigate(`/asset/${asset.id}`)}
                    className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-emerald-500 hover:shadow-lg cursor-pointer transition-all group"
                    data-testid={`asset-card-${asset.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-black font-semibold text-lg group-hover:text-emerald-400 transition-colors">
                          {asset.name}
                        </h3>
                        <p className="text-xs text-gray-600 mt-1">{asset.serial_no}</p>
                      </div>
                      <Badge variant={asset.asset_type === 'Mobile' ? 'secondary' : 'outline'} className="text-xs">
                        {asset.asset_type}
                      </Badge>
                    </div>

                    <div className="space-y-2">
                      {/* First row */}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Health Score</span>
                        <span className={`font-semibold ${getHealthColor(asset.health_score)}`}>
                          {asset.health_score.toFixed(2)}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Invoice Date</span>
                        <span className="text-black font-semibold text-xs">{formatDate(asset.invoice_date)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Safety</span>
                        <span className={`font-semibold ${getSafetyColor(asset.safety_score)}`}>
                          {asset.safety_score.toFixed(2)}%
                        </span>
                      </div>
                      
                      {/* Second row */}
                      <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-100">
                        <span className="text-gray-600">Warranty Deg.</span>
                        {user?.role === 'admin' ? (
                          <select
                            value={asset.warranty_degradation_rate}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleFieldUpdate(asset.id, 'warranty_degradation_rate', e.target.value);
                            }}
                            className="px-2 py-0.5 text-xs bg-white border border-gray-300 rounded text-black font-semibold"
                          >
                            <option value="1">1%/yr</option>
                            <option value="1.5">1.5%/yr</option>
                            <option value="2">2%/yr</option>
                            <option value="2.5">2.5%/yr</option>
                            <option value="3">3%/yr</option>
                            <option value="3.5">3.5%/yr</option>
                            <option value="4">4%/yr</option>
                            <option value="4.5">4.5%/yr</option>
                            <option value="5">5%/yr</option>
                          </select>
                        ) : (
                          <span className="text-black font-semibold">{asset.warranty_degradation_rate}%/yr</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Actual Deg.</span>
                        {user?.role === 'admin' ? (
                          <select
                            value={asset.actual_degradation_rate || 1.0}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleFieldUpdate(asset.id, 'actual_degradation_rate', e.target.value);
                            }}
                            className="px-2 py-0.5 text-xs bg-white border border-gray-300 rounded text-black font-semibold"
                          >
                            <option value="1">1%/yr</option>
                            <option value="1.5">1.5%/yr</option>
                            <option value="2">2%/yr</option>
                            <option value="2.5">2.5%/yr</option>
                            <option value="3">3%/yr</option>
                            <option value="3.5">3.5%/yr</option>
                            <option value="4">4%/yr</option>
                            <option value="4.5">4.5%/yr</option>
                            <option value="5">5%/yr</option>
                            <option value="5.5">5.5%/yr</option>
                            <option value="6">6%/yr</option>
                            <option value="6.5">6.5%/yr</option>
                            <option value="7">7%/yr</option>
                            <option value="7.5">7.5%/yr</option>
                            <option value="8">8%/yr</option>
                          </select>
                        ) : (
                          <span className="text-black font-semibold">{asset.actual_degradation_rate || 1.0}%/yr</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">SOH</span>
                        <span className={`font-semibold ${getHealthColor(asset.soh)}`}>
                          {asset.soh.toFixed(2)}%
                        </span>
                      </div>
                      
                      {/* Engineer Installer */}
                      <div className="text-xs text-gray-600 pt-2">
                        <div className="font-medium text-gray-700">Engineer Installer:</div>
                        <div className="text-gray-600 mt-1">{asset.engineer_installer}</div>
                      </div>
                      
                      {/* Checkboxes */}
                      <div className="pt-2 space-y-1.5 text-xs">
                        <label className="flex items-start space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={asset.installed_per_manual}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleCheckboxUpdate(asset.id, 'installed_per_manual', e.target.checked);
                            }}
                            disabled={user?.role === 'guest'}
                            className="mt-0.5"
                          />
                          <span className="text-gray-700 leading-tight">Installed per Operations Manual?</span>
                        </label>
                        <label className="flex items-start space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={asset.received_undamaged}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleCheckboxUpdate(asset.id, 'received_undamaged', e.target.checked);
                            }}
                            disabled={user?.role === 'guest'}
                            className="mt-0.5"
                          />
                          <span className="text-gray-700 leading-tight">Received undamaged before commissioning?</span>
                        </label>
                      </div>
                      
                      {/* 10-Year Degradation Projection Graph */}
                      <div className="pt-3 mt-3 border-t border-gray-100">
                        <div className="text-xs font-medium text-gray-700 mb-2">10-Year Degradation Projection</div>
                        <svg 
                          key={`graph-${asset.id}-${asset.warranty_degradation_rate}-${asset.actual_degradation_rate}`}
                          width="100%" 
                          height="100" 
                          viewBox="0 0 300 100" 
                          preserveAspectRatio="xMidYMid meet"
                        >
                          {/* Background grid */}
                          <defs>
                            <pattern id={`grid-${asset.id}`} width="30" height="20" patternUnits="userSpaceOnUse">
                              <path d="M 30 0 L 0 0 0 20" fill="none" stroke="#e5e7eb" strokeWidth="0.5"/>
                            </pattern>
                          </defs>
                          <rect width="300" height="100" fill="url(#grid-${asset.id})" />
                          
                          {/* Y-axis labels (SOH %) */}
                          <text x="5" y="15" fill="#6b7280" fontSize="8" fontWeight="bold">100%</text>
                          <text x="10" y="55" fill="#6b7280" fontSize="8">75%</text>
                          <text x="10" y="95" fill="#6b7280" fontSize="8">50%</text>
                          
                          {/* X-axis labels (Years) */}
                          <text x="35" y="98" fill="#6b7280" fontSize="7">Y0</text>
                          <text x="85" y="98" fill="#6b7280" fontSize="7">Y3</text>
                          <text x="135" y="98" fill="#6b7280" fontSize="7">Y5</text>
                          <text x="185" y="98" fill="#6b7280" fontSize="7">Y7</text>
                          <text x="235" y="98" fill="#6b7280" fontSize="7">Y10</text>
                          
                          {/* Warranty Degradation line (Green) */}
                          <polyline
                            points={(() => {
                              const degradationRate = asset.warranty_degradation_rate;
                              const points = [];
                              for (let year = 0; year <= 10; year++) {
                                const x = 40 + (year * 20);
                                const soh = Math.max(50, 100 - (year * degradationRate));
                                const y = 90 - ((soh - 50) / 50 * 80);
                                points.push(`${x},${y}`);
                              }
                              return points.join(' ');
                            })()}
                            fill="none"
                            stroke="#10b981"
                            strokeWidth="2"
                          />
                          
                          {/* Actual Degradation line (Orange/Red) */}
                          <polyline
                            points={(() => {
                              const actualRate = asset.actual_degradation_rate || 1.0;
                              const points = [];
                              for (let year = 0; year <= 10; year++) {
                                const x = 40 + (year * 20);
                                const soh = Math.max(50, 100 - (year * actualRate));
                                const y = 90 - ((soh - 50) / 50 * 80);
                                points.push(`${x},${y}`);
                              }
                              return points.join(' ');
                            })()}
                            fill="none"
                            stroke="#f97316"
                            strokeWidth="2"
                            strokeDasharray="4 2"
                          />
                          
                          {/* Current position marker */}
                          {(() => {
                            const commissionDate = new Date(asset.commissioning_date);
                            const now = new Date();
                            const yearsElapsed = Math.min(10, (now - commissionDate) / (365.25 * 24 * 60 * 60 * 1000));
                            const x = 40 + (yearsElapsed * 20);
                            const soh = Math.max(50, 100 - (yearsElapsed * asset.warranty_degradation_rate));
                            const y = 90 - ((soh - 50) / 50 * 80);
                            return (
                              <circle cx={x} cy={y} r="3" fill="#3b82f6" stroke="white" strokeWidth="1" />
                            );
                          })()}
                          
                          {/* Legend */}
                          <line x1="165" y1="12" x2="180" y2="12" stroke="#10b981" strokeWidth="2" />
                          <text x="185" y="15" fill="#6b7280" fontSize="7">Warranty</text>
                          
                          <line x1="165" y1="22" x2="180" y2="22" stroke="#f97316" strokeWidth="2" strokeDasharray="4 2" />
                          <text x="185" y="25" fill="#6b7280" fontSize="7">Actual</text>
                          
                          <circle cx="172" cy="32" r="2" fill="#3b82f6" />
                          <text x="185" y="35" fill="#6b7280" fontSize="7">Current</text>
                        </svg>
                        <div className="text-xs text-gray-500 mt-1 text-center">
                          Projected from commissioning
                        </div>
                      </div>

                      {/* SCA Alerts Indicator */}
                      {scaAlertsByAsset[asset.id] && scaAlertsByAsset[asset.id].length > 0 && (
                        <div className="pt-3 mt-3 border-t border-gray-100">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-xs font-medium text-gray-700">SCA Alerts</div>
                            <Badge className="bg-red-100 text-red-700 text-xs">
                              {scaAlertsByAsset[asset.id].length}
                            </Badge>
                          </div>
                          <div className="space-y-1">
                            {scaAlertsByAsset[asset.id].slice(0, 2).map((alert, idx) => (
                              <div key={idx} className="bg-red-50 border border-red-200 rounded p-2">
                                <div className="flex items-center justify-between">
                                  <span className={`text-xs font-semibold ${alert.severity === 'Critical' ? 'text-red-700' : 'text-yellow-700'}`}>
                                    {alert.alert_type}
                                  </span>
                                  <AlertTriangle className={`w-3 h-3 ${alert.severity === 'Critical' ? 'text-red-600' : 'text-yellow-600'}`} />
                                </div>
                                <p className="text-xs text-gray-600 mt-1 truncate">{alert.description.substring(0, 80)}...</p>
                              </div>
                            ))}
                            {scaAlertsByAsset[asset.id].length > 2 && (
                              <div className="text-xs text-gray-500 text-center mt-1">
                                +{scaAlertsByAsset[asset.id].length - 2} more alerts
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* 5-Day Weather Forecast */}
                      {weatherData[asset.id] && (
                        <div className="pt-3 mt-3 border-t border-gray-100">
                          <div className="text-xs font-medium text-gray-700 mb-2">5-Day Temperature Forecast</div>
                          <svg width="100%" height="80" viewBox="0 0 300 80" preserveAspectRatio="xMidYMid meet">
                            {/* Background */}
                            <rect width="300" height="80" fill="#f9fafb" />
                            
                            {/* Extreme temp zones */}
                            <rect x="40" y="0" width="250" height="15" fill="#fee2e2" opacity="0.5" />
                            <rect x="40" y="65" width="250" height="15" fill="#dbeafe" opacity="0.5" />
                            
                            {/* Threshold lines */}
                            <line x1="40" y1="15" x2="290" y2="15" stroke="#ef4444" strokeWidth="0.5" strokeDasharray="2 2" />
                            <text x="5" y="18" fill="#ef4444" fontSize="7">40°C</text>
                            <line x1="40" y1="65" x2="290" y2="65" stroke="#3b82f6" strokeWidth="0.5" strokeDasharray="2 2" />
                            <text x="5" y="68" fill="#3b82f6" fontSize="7">-5°C</text>
                            
                            {/* Temperature bars and values */}
                            {weatherData[asset.id].forecast.slice(0, 5).map((day, index) => {
                              const x = 50 + (index * 50);
                              const tempRange = 50; // Scale: -10 to 50 degrees
                              const yMax = 70 - ((day.temp_max + 10) / tempRange * 50);
                              const yMin = 70 - ((day.temp_min + 10) / tempRange * 50);
                              const barHeight = Math.abs(yMin - yMax);
                              
                              // Color based on temperature extremes
                              let barColor = "#10b981"; // Green for normal
                              if (day.temp_max > 40) barColor = "#ef4444"; // Red for hot
                              if (day.temp_min < -5) barColor = "#3b82f6"; // Blue for cold
                              
                              return (
                                <g key={index}>
                                  {/* Temperature bar */}
                                  <rect 
                                    x={x - 8} 
                                    y={yMax} 
                                    width="16" 
                                    height={barHeight} 
                                    fill={barColor} 
                                    opacity="0.7"
                                  />
                                  {/* Max temp */}
                                  <text x={x} y={yMax - 2} fill="#374151" fontSize="6" textAnchor="middle" fontWeight="bold">
                                    {day.temp_max.toFixed(0)}°
                                  </text>
                                  {/* Min temp */}
                                  <text x={x} y={yMin + 8} fill="#6b7280" fontSize="5" textAnchor="middle">
                                    {day.temp_min.toFixed(0)}°
                                  </text>
                                  {/* Day label */}
                                  <text x={x} y="78" fill="#6b7280" fontSize="6" textAnchor="middle">
                                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                                  </text>
                                </g>
                              );
                            })}
                          </svg>
                          <div className="text-xs text-gray-500 mt-1 text-center">
                            {weatherData[asset.id].alerts_generated?.length > 0 && (
                              <span className="text-red-600 font-semibold">⚠ Extreme temperature alert</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-600 flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      {asset.location.split(',')[1]?.trim() || asset.location}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Create Asset Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-black">Create New Asset</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAsset} className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="asset-name" className="text-gray-700">Asset Name</Label>
                <Input
                  id="asset-name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., SodiumQ-Melbourne South"
                  required
                  className="bg-white border-gray-300 text-black"
                  data-testid="asset-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="asset-type" className="text-gray-700">Asset Type</Label>
                <Select value={formData.asset_type} onValueChange={(value) => setFormData({...formData, asset_type: value})}>
                  <SelectTrigger className="bg-white border-gray-300 text-black" data-testid="asset-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="Fixed">Fixed</SelectItem>
                    <SelectItem value="Mobile">Mobile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="asset-location" className="text-gray-700">Location Address</Label>
              <Input
                id="asset-location"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                placeholder="e.g., 123 Main St, Melbourne VIC 3000"
                required
                className="bg-white border-gray-300 text-black"
                data-testid="asset-location-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="asset-latitude" className="text-gray-700">Latitude</Label>
                <Input
                  id="asset-latitude"
                  type="number"
                  step="0.000001"
                  value={formData.latitude}
                  onChange={(e) => setFormData({...formData, latitude: e.target.value})}
                  placeholder="-37.8136"
                  required
                  className="bg-white border-gray-300 text-black"
                  data-testid="asset-latitude-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="asset-longitude" className="text-gray-700">Longitude</Label>
                <Input
                  id="asset-longitude"
                  type="number"
                  step="0.000001"
                  value={formData.longitude}
                  onChange={(e) => setFormData({...formData, longitude: e.target.value})}
                  placeholder="144.9631"
                  required
                  className="bg-white border-gray-300 text-black"
                  data-testid="asset-longitude-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="asset-serial" className="text-gray-700">Serial Number</Label>
              <Input
                id="asset-serial"
                value={formData.serial_no}
                onChange={(e) => setFormData({...formData, serial_no: e.target.value})}
                placeholder="NAS-2024021"
                required
                className="bg-white border-gray-300 text-black"
                data-testid="asset-serial-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="asset-manufacture" className="text-gray-700">Manufacture Date</Label>
                <Input
                  id="asset-manufacture"
                  type="date"
                  value={formData.manufacture_date || ''}
                  onChange={(e) => setFormData({...formData, manufacture_date: e.target.value})}
                  required
                  className="bg-white border-gray-300 text-black"
                  data-testid="asset-manufacture-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="asset-commission" className="text-gray-700">Commissioning Date</Label>
                <Input
                  id="asset-commission"
                  type="date"
                  value={formData.commissioning_date}
                  onChange={(e) => setFormData({...formData, commissioning_date: e.target.value})}
                  required
                  className="bg-white border-gray-300 text-black"
                  data-testid="asset-commission-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="asset-warranty" className="text-gray-700">Warranty Degradation Rate (% per year)</Label>
              <Input
                id="asset-warranty"
                type="number"
                step="0.1"
                value={formData.warranty_degradation_rate}
                onChange={(e) => setFormData({...formData, warranty_degradation_rate: parseFloat(e.target.value)})}
                placeholder="1.0"
                required
                className="bg-white border-gray-300 text-black"
                data-testid="asset-warranty-input"
              />
              <p className="text-xs text-gray-500">Standard warranty: 1% per year. Higher values may trigger warranty claims.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="asset-actual" className="text-gray-700">Actual Degradation Rate (% per year)</Label>
              <Input
                id="asset-actual"
                type="number"
                step="0.1"
                value={formData.actual_degradation_rate}
                onChange={(e) => setFormData({...formData, actual_degradation_rate: parseFloat(e.target.value)})}
                placeholder="1.0"
                required
                className="bg-white border-gray-300 text-black"
                data-testid="asset-actual-input"
              />
              <p className="text-xs text-gray-500">Real-world degradation rate. Can be up to 8% per year.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="asset-invoice" className="text-gray-700">Invoice Date</Label>
              <Input
                id="asset-invoice"
                type="date"
                value={formData.invoice_date || ''}
                onChange={(e) => setFormData({...formData, invoice_date: e.target.value})}
                required
                className="bg-white border-gray-300 text-black"
                data-testid="asset-invoice-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="asset-installer" className="text-gray-700">Engineer Installer</Label>
              <select
                id="asset-installer"
                value={formData.engineer_installer}
                onChange={(e) => setFormData({...formData, engineer_installer: e.target.value})}
                required
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-black"
                data-testid="asset-installer-select"
              >
                <option value="">Select installer...</option>
                {engineerInstallers.map((installer) => (
                  <option key={installer} value={installer}>{installer}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="asset-owner" className="text-gray-700">Owner</Label>
              <Input
                id="asset-owner"
                value={formData.owner}
                onChange={(e) => setFormData({...formData, owner: e.target.value})}
                placeholder="SodiumQ Operations"
                required
                className="bg-white border-gray-300 text-black"
                data-testid="asset-owner-input"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="border-gray-300 text-gray-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                data-testid="asset-submit-btn"
              >
                Create Asset
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
